import React, { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SponsorPackageCard from "@/components/SponsorPackageCard";
import LeadForm from "@/components/LeadForm";
import { api } from "@/lib/api";
import { Download, Eye, Users, Tv, Target, Shield, Hexagon } from "lucide-react";

const SPONSOR_BENEFITS = [
  { Icon: Eye,     title: "Brand Visibility",       desc: "Centre-ice, jerseys, helmets, arena LEDs, OTT bumpers, broadcast graphics." },
  { Icon: Users,   title: "Youth Engagement",       desc: "Academy network, school partnerships, district leagues, scholarships." },
  { Icon: Tv,      title: "Digital Content",        desc: "Co-branded reels, fan-cam features, athlete creator content, OTT inserts." },
  { Icon: Target,  title: "On-ground Branding",     desc: "Rink-boards, centre-ice logo print, hospitality boxes, fan-zone activations." },
  { Icon: Shield,  title: "CSR Alignment",          desc: "Grassroots, women's pathway, equipment grants, scholarship-linked sponsorships." },
  { Icon: Hexagon, title: "Community Sports",       desc: "Long-term association with a community sport movement across 12+ cities." },
];

export default function Sponsorships() {
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get("/sponsorship-tiers").then((r) => setTiers(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageMeta
        title="Sponsor PSHL — Reach 1.4 Billion"
        description="9 sponsorship tracks across league, teams, jerseys, arenas and broadcast. Premium sponsor inventory for brands that want pan-India visibility."
        image="/api/og/sponsorships/og-image.png"
      />
      <PageHero
        overline="Commercial Tracks"
        title={<>Sponsor PSHL. <span className="text-gold-metallic">Reach 1.4 Billion.</span></>}
        subtitle="Premium sponsor inventory across the league, teams, jerseys, arenas and broadcast — designed for brands that want pan-India visibility, CSR alignment, and youth engagement."
        bgImage="https://images.pexels.com/photos/15885025/pexels-photo-15885025.jpeg"
      />

      <section className="section pt-0">
        <div className="pshl-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiers.map((p, i) => <SponsorPackageCard key={p.id} pkg={p} index={i} onCta={() => document.getElementById("sponsor-lead")?.scrollIntoView({ behavior: "smooth" })} />)}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#" data-testid="sponsor-deck-cta" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#BF953F] to-[#FBF5B7] text-black font-display uppercase tracking-[0.18em] text-xs px-6 py-4 hover:brightness-110">
              <Download className="h-3.5 w-3.5" /> Download Sponsorship Deck
            </a>
            <a href="#sponsor-lead" className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-6 py-4">
              Request Partnership Call
            </a>
          </div>
        </div>
      </section>

      <section className="section bg-[#08090E]">
        <div className="pshl-container">
          <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Why Sponsor PSHL</div>
          <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Premium Sponsor Benefits.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SPONSOR_BENEFITS.map(({ Icon, title, desc }) => (
              <div key={title} className="bg-pshl-surface border border-white/10 hover:border-pshl-electric transition-colors p-6">
                <div className="h-10 w-10 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40"><Icon className="h-5 w-5 text-pshl-electric" /></div>
                <div className="font-display text-xl text-white mt-4">{title}</div>
                <div className="text-sm text-white/65 mt-2 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRANCHISE OWNERSHIP */}
      <section id="franchise" className="section">
        <div className="pshl-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Franchise Ownership</div>
            <h2 className="font-display text-4xl md:text-5xl text-white tracking-tighter">Own a <span className="text-gold-metallic">PSHL8 Team.</span></h2>
            <p className="mt-4 text-white/75 leading-relaxed">A long-term equity position in India’s emerging pro skate hockey league. City-state team rights, sponsorship revenue, merchandise, academy network, player IP and a share of broadcast revenue.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                "Local team rights", "Local sponsorships", "Merchandise",
                "Academy network", "Player development", "Revenue sharing",
                "League participation", "10-year term",
              ].map((b) => (
                <div key={b} className="border border-white/8 bg-white/[0.02] px-4 py-3 text-sm text-white/85">{b}</div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 bg-pshl-surface border border-white/10 p-8">
            <div className="font-display text-xs uppercase tracking-[0.22em] text-pshl-electric">Investor Snapshot</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[["Format","City-Franchise"],["Term","10 years"],["Revenue","Multi-stream"],["Audience","16–45 yrs"],["Markets","Tier 1+2"],["Inclusion","Mixed gender"]].map(([k,v]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/55">{k}</div>
                  <div className="font-display text-xl text-white mt-1">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-sm text-white/70 leading-relaxed">Apply via the inquiry form below or download the franchise deck. Our partnerships team responds within 24 hours.</div>
          </div>
        </div>
      </section>

      <section id="sponsor-lead" className="section bg-[#08090E]">
        <div className="pshl-container grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-3">Request a Partnership Call</div>
            <h2 className="font-display text-4xl text-white tracking-tighter">Let’s Talk Sponsorship.</h2>
            <p className="mt-4 text-white/75 leading-relaxed">Tell us about your brand, scope, and timelines. Our team will respond with a tailored partnership track.</p>
          </div>
          <div className="lg:col-span-7"><LeadForm defaultInterest="sponsor" /></div>
        </div>
      </section>
    </div>
  );
}
